/**
 * API Keys Page (Business Tier Only)
 * Allows users to create and manage API keys
 */
import React, { useState, useEffect } from 'react';
import { Key, Plus, Copy, Trash2, Loader2, Check, AlertCircle, Clock, Activity } from 'lucide-react';
import toast from 'react-hot-toast';
import { DashboardLayout } from '../../components/dashboard/DashboardLayout';
import { Button } from '../../components/Button';
import { usePermissions } from '../../hooks/usePermissions';
import { apiKeysService } from '../../api/services';
import { copyToClipboard } from '../../utils/clipboard';
import { API_BASE_URL } from '../../utils/constants';
import type { APIKey, APIKeyCreateResponse } from '../../types';

const ALL_PERMISSIONS = ['qr:read', 'qr:create', 'qr:update', 'analytics:read'] as const;
const PERMISSION_LABELS: Record<string, string> = {
  'qr:read': 'Read QR codes',
  'qr:create': 'Create QR codes',
  'qr:update': 'Update QR codes',
  'analytics:read': 'Read analytics',
};

export default function APIKeysPage() {
  const { hasApiAccess, planDisplayName } = usePermissions();
  const [keys, setKeys] = useState<APIKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [createModal, setCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyDescription, setNewKeyDescription] = useState('');
  const [newKeyPermissions, setNewKeyPermissions] = useState<string[]>([...ALL_PERMISSIONS]);
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState<APIKeyCreateResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);

  useEffect(() => {
    if (hasApiAccess) {
      loadKeys();
    } else {
      setLoading(false);
    }
  }, [hasApiAccess]);

  const loadKeys = async () => {
    try {
      const response = await apiKeysService.listKeys();
      setKeys(response);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newKeyName.trim()) {
      toast.error('Please enter a name for the API key');
      return;
    }

    if (newKeyPermissions.length === 0) {
      toast.error('Select at least one permission');
      return;
    }

    setCreating(true);
    try {
      const response = await apiKeysService.createKey({
        name: newKeyName,
        description: newKeyDescription || undefined,
        permissions: newKeyPermissions,
      });
      setNewKey(response);
      loadKeys();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to create API key');
      setCreating(false);
    }
  };

  const handleCopyKey = async () => {
    if (newKey?.api_key) {
      try {
        await copyToClipboard(newKey.api_key);
        setCopied(true);
        toast.success('API key copied to clipboard');
        setTimeout(() => setCopied(false), 2000);
      } catch (error) {
        toast.error('Failed to copy API key');
      }
    }
  };

  const handleDelete = async (keyId: number) => {
    setDeleting(keyId);
    try {
      await apiKeysService.revokeKey(keyId);
      toast.success('API key revoked');
      setKeys(keys.filter((k) => k.id !== keyId));
      setDeleteConfirm(null);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to revoke API key');
    } finally {
      setDeleting(null);
    }
  };

  const closeCreateModal = () => {
    setCreateModal(false);
    setNewKeyName('');
    setNewKeyDescription('');
    setNewKeyPermissions([...ALL_PERMISSIONS]);
    setNewKey(null);
    setCreating(false);
    setCopied(false);
  };

  const togglePermission = (permission: string) => {
    setNewKeyPermissions((prev) =>
      prev.includes(permission) ? prev.filter((p) => p !== permission) : [...prev, permission]
    );
  };

  // No API access - show upgrade prompt
  if (!hasApiAccess) {
    return (
      <DashboardLayout currentPage="api-keys">
        <div className="p-8 flex items-center justify-center min-h-[60vh]">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-[--clinqr-bg-subtle] rounded-full flex items-center justify-center mx-auto mb-4">
              <Key className="w-8 h-8 text-[--clinqr-text-secondary]" />
            </div>
            <h2 className="text-xl font-semibold text-[--clinqr-text-primary] mb-2">API Access Required</h2>
            <p className="text-[--clinqr-text-secondary] mb-6">
              API access is available on the Business plan. Upgrade to create API keys and integrate
              Clinqr with your applications.
            </p>
            <p className="text-sm text-[--clinqr-text-secondary] mb-4">
              Current plan: <span className="font-medium">{planDisplayName}</span>
            </p>
            <a
              href="/dashboard/billing"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[--clinqr-primary] text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
            >
              Upgrade to Business
            </a>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout currentPage="api-keys">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="heading-1 mb-1">API Keys</h1>
            <p className="text-[--clinqr-text-secondary]">Manage your API keys for programmatic access</p>
          </div>
          <Button onClick={() => setCreateModal(true)}>
            <Plus className="w-4 h-4" />
            Create API Key
          </Button>
        </div>

        {/* API Documentation Link */}
        <div className="bg-[--clinqr-bg-subtle] border border-[--clinqr-border]/10 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-[--clinqr-primary] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-[--clinqr-text-primary]">API Documentation</p>
              <p className="text-sm text-[--clinqr-text-secondary]">
                Use the API to create QR codes, read analytics, and more. Base URL:{' '}
                <code className="bg-white border border-[--clinqr-border]/10 px-1 rounded">
                  {API_BASE_URL}/api/v1
                </code>
              </p>
              <div className="flex items-center gap-4 mt-1">
                <a
                  href={`${API_BASE_URL}/v1/docs`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-[--clinqr-text-primary] underline hover:text-[--clinqr-primary-light] inline-block"
                >
                  View full API reference &rarr;
                </a>
                <a
                  href={`${API_BASE_URL}/v1/mcp-docs`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-[--clinqr-text-primary] underline hover:text-[--clinqr-primary-light] inline-block"
                >
                  Connect via MCP &rarr;
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Keys List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[--clinqr-primary]" />
          </div>
        ) : keys.length === 0 ? (
          <div className="bg-white rounded-xl border border-[--clinqr-border] p-12 text-center">
            <Key className="w-12 h-12 text-[--clinqr-text-muted] mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[--clinqr-text-primary] mb-2">No API Keys</h3>
            <p className="text-[--clinqr-text-secondary] mb-4">Create your first API key to get started</p>
            <Button onClick={() => setCreateModal(true)} className="mx-auto">
              <Plus className="w-4 h-4" />
              Create API Key
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {keys.map((key) => (
              <div
                key={key.id}
                className="bg-white rounded-xl border border-[--clinqr-border] p-5 hover:border-[--clinqr-text-muted] transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[--clinqr-bg-subtle] rounded-lg flex items-center justify-center">
                      <Key className="w-5 h-5 text-[--clinqr-text-secondary]" />
                    </div>
                    <div>
                      <h3 className="font-medium text-[--clinqr-text-primary]">{key.name}</h3>
                      {key.description && (
                        <p className="text-sm text-[--clinqr-text-secondary] mt-0.5">{key.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-2">
                        <code className="text-sm bg-[--clinqr-bg-subtle] px-2 py-1 rounded text-[--clinqr-text-secondary]">
                          {key.key_prefix}...
                        </code>
                        <span className="flex items-center gap-1 text-xs text-[--clinqr-text-secondary]">
                          <Activity className="w-3 h-3" />
                          {key.request_count.toLocaleString()} requests
                        </span>
                        {key.last_used_at && (
                          <span className="flex items-center gap-1 text-xs text-[--clinqr-text-secondary]">
                            <Clock className="w-3 h-3" />
                            Last used {new Date(key.last_used_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {deleteConfirm === key.id ? (
                      <>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setDeleteConfirm(null)}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDelete(key.id)}
                          disabled={deleting === key.id}
                        >
                          {deleting === key.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            'Confirm'
                          )}
                        </Button>
                      </>
                    ) : (
                      <button
                        onClick={() => setDeleteConfirm(key.id)}
                        className="p-2 text-[--clinqr-text-secondary] hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Revoke key"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create Modal */}
        {createModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-md">
              {newKey ? (
                // Show the new key
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Check className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-[--clinqr-text-primary]">API Key Created</h3>
                      <p className="text-sm text-[--clinqr-text-secondary]">{newKey.name}</p>
                    </div>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-amber-800">
                      <strong>Important:</strong> Copy this key now. You won't be able to see it
                      again.
                    </p>
                  </div>

                  <div className="relative mb-4">
                    <input
                      type="text"
                      value={newKey.api_key}
                      readOnly
                      className="w-full px-3 py-2 pr-10 font-mono text-sm bg-[--clinqr-bg-subtle] border border-[--clinqr-border] rounded-lg"
                    />
                    <button
                      onClick={handleCopyKey}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-[--clinqr-border] rounded"
                    >
                      {copied ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4 text-[--clinqr-text-secondary]" />
                      )}
                    </button>
                  </div>

                  <Button onClick={closeCreateModal} className="w-full">
                    Done
                  </Button>
                </>
              ) : (
                // Create form
                <>
                  <h3 className="text-lg font-semibold text-[--clinqr-text-primary] mb-6">Create API Key</h3>

                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-[--clinqr-text-primary] mb-1">
                        Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newKeyName}
                        onChange={(e) => setNewKeyName(e.target.value)}
                        placeholder="e.g., Production API Key"
                        className="w-full px-3 py-2 border border-[--clinqr-border] rounded-lg focus:outline-none focus:ring-2 focus:ring-[--clinqr-primary]/20 focus:border-[--clinqr-primary]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[--clinqr-text-primary] mb-1">
                        Description
                      </label>
                      <textarea
                        value={newKeyDescription}
                        onChange={(e) => setNewKeyDescription(e.target.value)}
                        placeholder="What will this key be used for?"
                        rows={2}
                        className="w-full px-3 py-2 border border-[--clinqr-border] rounded-lg focus:outline-none focus:ring-2 focus:ring-[--clinqr-primary]/20 focus:border-[--clinqr-primary]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[--clinqr-text-primary] mb-1">
                        Permissions
                      </label>
                      <p className="text-xs text-[--clinqr-text-secondary] mb-2">
                        If this key is going into an AI agent or MCP client, select only "Read QR
                        codes" and "Read analytics" — never give an agent a key with create/update
                        permissions.
                      </p>
                      <div className="space-y-2">
                        {ALL_PERMISSIONS.map((permission) => (
                          <label key={permission} className="flex items-center gap-2 text-sm text-[--clinqr-text-primary]">
                            <input
                              type="checkbox"
                              checked={newKeyPermissions.includes(permission)}
                              onChange={() => togglePermission(permission)}
                              className="rounded border-[--clinqr-border]"
                            />
                            {PERMISSION_LABELS[permission]}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button variant="secondary" onClick={closeCreateModal} className="flex-1">
                      Cancel
                    </Button>
                    <Button
                      onClick={handleCreate}
                      disabled={creating || !newKeyName.trim()}
                      className="flex-1"
                    >
                      {creating ? (
                        <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                      ) : (
                        'Create Key'
                      )}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
